import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Ban, FileText, FlaskConical, HandCoins, Plus, Send, X } from "lucide-react";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Dialog } from "../../components/Dialog";
import { Badge } from "../../components/Badge";
import { EmptyState } from "../../components/EmptyState";
import { useToast } from "../../components/Toast";
import { api } from "../../api/client";
import { useAuth } from "../auth/useAuth";
import type {
  EInvoiceMode,
  EInvoiceStatus,
  Invoice,
  InvoiceList,
  InvoiceRequest,
} from "../../api/types";
import { formatMoney, toDecimal } from "../../lib/money";
import { MoneyField } from "../../components/MoneyInput";

// 財務的發票頁(第 15 輪 Task 5)。三個分頁:**待開立 / 已開立 / 逾期未收**。
//
// 逾期未收單獨一塊不是排版偏好 —— 路線圖 §3.3:「那是財務每天真正在追的東西」。
// 混在已開立裡的話,一張拖了三個月的發票跟昨天剛開的排在一起,看不出輕重。
//
// --------------------------------------------------------------------------
// 兩顆鈕的差別要在按下去之前就看得出來
// --------------------------------------------------------------------------
// **「系統開立」開不出過去的發票。** Amego 的 `/json/f0401` 沒有日期欄位
// (路線圖 §7.3,2026-09-16 實地打過測試環境確認),發票日就是打 API 那一刻。
// 所以想要的日期是過去、或憑證還沒設好時,那顆一定是 disabled 而且 title 說得出
// 原因 —— 讓他按下去才收到 409,他會以為是系統壞了。
//
// **「手動登錄」永遠按得動。** 它一個字都不打 Amego,正式機在拿到正式憑證之前
// 就只有這條路。

type Tab = "pending" | "issued" | "overdue";

const TABS: Array<{ key: Tab; label: string }> = [
  { key: "pending", label: "待開立" },
  { key: "issued", label: "已開立" },
  { key: "overdue", label: "逾期未收" },
];

/** 金額一律走 decimal.js。`Number` 算出來的角分會直接印給財務看。 */
function money(value: string | number): string {
  // 全系統金額一律 0,000,000、四捨五入到整數(業主 2026-10-15)
  return formatMoney(value);
}

export function InvoicesPage() {
  // 財務點「申請開發票」通知進來是 `/invoices?request=<id>`:待開立分頁、直接打開那一筆的登錄視窗(2026-10-15)。
  const [searchParams, setSearchParams] = useSearchParams();
  const openRequestId = Number(searchParams.get("request")) || null;
  const [tab, setTab] = useState<Tab>("pending");
  const toast = useToast();
  const qc = useQueryClient();
  const { user } = useAuth();
  const isOwner = user?.role === "OWNER";

  const einvoice = useQuery({
    queryKey: ["einvoice-status"],
    queryFn: () => api.get<EInvoiceStatus>("/settings/einvoice"),
  });
  const configured = einvoice.data?.configured ?? false;
  const mode: EInvoiceMode = einvoice.data?.mode ?? "TEST";

  const switchMode = useMutation({
    mutationFn: (next: EInvoiceMode) =>
      api.put<EInvoiceStatus>("/settings/einvoice", { mode: next }),
    onSuccess: (got) => {
      toast.push(got.mode === "TEST" ? "已切換成測試模式" : "已切換成正式模式", "success");
      void qc.invalidateQueries({ queryKey: ["einvoice-status"] });
    },
    onError: (err) => toast.push(err instanceof Error ? err.message : "切換失敗", "error"),
  });

  const requests = useQuery({
    queryKey: ["invoice-requests", "PENDING"],
    queryFn: () => api.get<InvoiceRequest[]>("/invoice-requests", { status: "PENDING" }),
  });
  const issued = useQuery({
    queryKey: ["invoices"],
    queryFn: () => api.get<InvoiceList>("/invoices"),
  });
  const overdue = useQuery({
    queryKey: ["invoices", "overdue"],
    queryFn: () => api.get<InvoiceList>("/invoices", { overdue: "true" }),
  });

  const overdueCount = overdue.data?.items.length ?? 0;

  const refreshAll = () => {
    void qc.invalidateQueries({ queryKey: ["invoice-requests"] });
    void qc.invalidateQueries({ queryKey: ["invoices"] });
  };

  const issueApi = useMutation({
    mutationFn: (req: InvoiceRequest) =>
      api.post<Invoice>(`/invoice-requests/${req.id}/issue`),
    onSuccess: (inv) => {
      toast.push(`發票 ${inv.invoice_no} 已開立`, "success");
      refreshAll();
    },
    onError: (err) => toast.push(err instanceof Error ? err.message : "開立失敗", "error"),
  });

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-4">
      <h2 className="m-0 text-xl font-semibold text-[var(--text)]">發票</h2>

      {/* **模式要大聲講。** 財務按下「系統開立」之前一定要知道這一張會不會
          真的送進國稅局 —— 這一條橫幅不可以只靠顏色,所以模式兩個字直接寫出來。 */}
      <div
        role="status"
        className={
          "flex flex-wrap items-center justify-between gap-2 rounded-[var(--radius)] border px-3 py-2 text-sm " +
          (mode === "TEST"
            ? "border-[var(--warn-border,var(--border))] bg-[var(--surface-2)] text-[var(--text)]"
            : "border-[var(--danger)] bg-[var(--surface-2)] text-[var(--text)]")
        }
      >
        <span className="flex items-center gap-2">
          {mode === "TEST" ? <FlaskConical aria-hidden="true" size={16} /> : null}
          <strong>{mode === "TEST" ? "測試模式" : "正式模式"}</strong>
          <span className="text-[var(--text-2)]">
            {mode === "TEST"
              ? "開出來的是演練用的發票,不會送進國稅局,也不會算進營收。"
              : "開出來的是真的發票,會送進國稅局。"}
          </span>
        </span>
        {isOwner ? (
          <Button
            size="sm"
            loading={switchMode.isPending}
            onClick={() => switchMode.mutate(mode === "TEST" ? "LIVE" : "TEST")}
          >
            切換成{mode === "TEST" ? "正式" : "測試"}模式
          </Button>
        ) : (
          // 財務看得到模式,但切不動 —— 這個開關決定的是會不會真的開票出去。
          <span className="text-xs text-[var(--text-3)]">模式只有老闆切得動</span>
        )}
      </div>

      {/* 給會計看的一句話。發票金額與報價單的期別金額會差到幾毛,
          那不是系統算錯 —— 是電子發票開不出角分(Amego 實測)。 */}
      <p className="m-0 text-xs text-[var(--text-3)]">
        電子發票只開得出整數元,所以發票金額會是期別金額四捨五入後的整數。
        對帳時兩邊差幾毛是正常的。
      </p>

      {!configured ? (
        // 正式機在拿到正式憑證之前就是這個狀態。**說清楚原因**,
        // 不然財務只會看到一顆按不動的鈕,以為是系統壞了。
        <p
          role="status"
          className="m-0 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface-2)] px-3 py-2 text-sm text-[var(--text-2)]"
        >
          還沒有設定電子發票憑證,目前只能用「手動登錄」把在別處開好的發票填回系統。
        </p>
      ) : null}

      <div role="tablist" aria-label="發票分頁" className="flex gap-1 border-b border-[var(--border)]">
        {TABS.map((item) => (
          <button
            key={item.key}
            role="tab"
            type="button"
            aria-selected={tab === item.key}
            onClick={() => setTab(item.key)}
            className={
              "-mb-px border-b-2 px-3 py-2 text-sm font-medium " +
              (tab === item.key
                ? "border-[var(--accent)] text-[var(--text)]"
                : "border-transparent text-[var(--text-2)] hover:text-[var(--text)]")
            }
          >
            {item.label}
            {/* 逾期的數字帶在標籤上 —— 不點進去也知道有沒有事要追。
                **數字不是只靠顏色**:那顆徽章上有數,螢幕閱讀器也讀得到。 */}
            {item.key === "overdue" && overdueCount > 0 ? (
              // 用既有的 Badge 而不是自己捏一顆:顏色一律走代幣,
              // 寫死 text-white 在深色主題下會變成白底白字(tokens-contrast 守著這條)。
              <span className="ml-1.5 align-middle">
                <Badge tone="danger">{overdueCount}</Badge>
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {tab === "pending" ? (
        <PendingTab
          rows={requests.data ?? []}
          loaded={requests.isSuccess}
          configured={configured}
          onIssue={(req) => issueApi.mutate(req)}
          issuing={issueApi.isPending}
          onDone={refreshAll}
          openRequestId={openRequestId}
          onOpened={() => setSearchParams({}, { replace: true })}
        />
      ) : null}
      {tab === "issued" ? (
        <IssuedTab list={issued.data} onDone={refreshAll} />
      ) : null}
      {tab === "overdue" ? (
        <IssuedTab list={overdue.data} overdue onDone={refreshAll} />
      ) : null}
    </div>
  );
}

/** 報價單號,點了去那一張報價單。 */
function QuoteLink({ id, no }: { id: number; no: string }) {
  return (
    <Link to={`/quotes/${id}`} className="whitespace-nowrap font-medium text-[var(--accent)] underline-offset-2 hover:underline">
      {no}
    </Link>
  );
}

/* ---------------------------------------------------------------- 待開立 */

interface PendingProps {
  rows: InvoiceRequest[];
  configured: boolean;
  onIssue: (req: InvoiceRequest) => void;
  issuing: boolean;
  onDone: () => void;
  loaded?: boolean;
  /** 從通知進來要直接打開的那一筆;打開之後叫 `onOpened` 把網址清掉。 */
  openRequestId?: number | null;
  onOpened?: () => void;
}

function PendingTab({ rows, configured, onIssue, issuing, onDone, loaded = true, openRequestId = null, onOpened }: PendingProps) {
  const [manual, setManual] = useState<InvoiceRequest | null>(null);
  const [gone, setGone] = useState(false);

  useEffect(() => {
    if (openRequestId === null || !loaded) return;
    const hit = rows.find((row) => row.id === openRequestId);
    if (hit) setManual(hit);
    else setGone(true);  // 已經開過或被撤銷了
    onOpened?.();
  }, [openRequestId, loaded, rows, onOpened]);

  const goneNote = gone ? (
    <p role="status" className="m-0 text-sm text-[var(--text-2)]">那筆開票申請已經開過或撤銷了,不在待開立清單上。</p>
  ) : null;

  if (rows.length === 0) {
    return (
      <>
        {goneNote}
        <EmptyState title="目前沒有待開立的申請" description="同仁把報價單結案後提出申請,就會出現在這裡。" />
      </>
    );
  }

  return (
    <>
      {goneNote}
      <div className="w-full overflow-x-auto rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)]">
        <table aria-label="待開立清單" className="w-full border-collapse text-left text-sm">
          <thead>
            <tr className="whitespace-nowrap bg-[var(--surface-2)] text-xs font-medium text-[var(--text-2)]">
              <th scope="col" className="px-3 py-2">報價單</th>
              <th scope="col" className="px-3 py-2">期別</th>
              <th scope="col" className="px-3 py-2">申請金額</th>
              <th scope="col" className="px-3 py-2">想要的日期</th>
              <th scope="col" className="px-3 py-2">申請人</th>
              <th scope="col" className="px-3 py-2">動作</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((req) => {
              // 兩個各自獨立的理由,訊息也要分開 —— 合成一句「不能開立」的話,
              // 財務不知道是等憑證還是要改走手開。
              const reason = !configured
                ? "還沒有設定電子發票憑證,請改用「手動登錄」"
                : req.needs_manual_issue
                  ? "電子發票只開得出今天的,這筆要的是過去的日期,請改用「手動登錄」"
                  : "";
              return (
                <tr key={req.id} className="border-t border-[var(--border)]">
                  <td className="px-3 py-2">
                    {/* 單號點得進那一張報價單(業主 2026-10-16) */}
                    <QuoteLink id={req.quote_id} no={req.quote_no} />
                    <div className="text-xs text-[var(--text-3)]">{req.quote_title}</div>
                  </td>
                  <td className="px-3 py-2">
                    {req.payment_term_name ? `第 ${req.payment_term_seq} 期 ${req.payment_term_name}` : "—"}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 tabular-nums">{money(req.amount)}</td>
                  <td className="whitespace-nowrap px-3 py-2">{req.wanted_date ?? "開今天的"}</td>
                  <td className="px-3 py-2">{req.requester_name}</td>
                  <td className="whitespace-nowrap px-3 py-2">
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="primary"
                        icon={<Send aria-hidden="true" size={14} />}
                        disabled={Boolean(reason) || issuing}
                        title={reason || undefined}
                        onClick={() => onIssue(req)}
                      >
                        系統開立
                      </Button>
                      <Button
                        size="sm"
                        icon={<FileText aria-hidden="true" size={14} />}
                        onClick={() => setManual(req)}
                      >
                        手動登錄
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <ManualDialog
        key={manual?.id ?? "none"}
        request={manual}
        onClose={() => setManual(null)}
        onDone={() => {
          setManual(null);
          onDone();
        }}
      />
    </>
  );
}

/* ---------------------------------------------------------------- 手動登錄 */

function ManualDialog({
  request,
  onClose,
  onDone,
}: {
  request: InvoiceRequest | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [no, setNo] = useState("");
  // 申請有指定日期就用那天,沒有就是今天(台北)
  const [when, setWhen] = useState(request?.wanted_date ?? new Date().toLocaleDateString("sv-SE", { timeZone: "Asia/Taipei" }));
  const [amount, setAmount] = useState(request?.amount ?? "");
  // 這筆申請開了不只一張:按「+」多一張(業主 2026-10-15)。同一個發票日、同一個買受人
  const [more, setMore] = useState<{ no: string; amount: string }[]>([]);
  const total = [amount, ...more.map((row) => row.amount)].reduce(
    (sum, value) => sum.plus(toDecimal(value)), toDecimal(0),
  );
  const addRow = () => {
    // 新的那一張預設是還沒分到的金額
    const left = toDecimal(request?.amount ?? 0).minus(total);
    setMore((rows) => [...rows, { no: "", amount: left.greaterThan(0) ? left.toFixed(0) : "" }]);
  };
  const setRow = (index: number, patch: Partial<{ no: string; amount: string }>) =>
    setMore((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  const ready = Boolean(no.trim()) && Boolean(when) && more.every((row) => row.no.trim() && row.amount.trim());

  const save = useMutation({
    mutationFn: () =>
      api.post<Invoice>(`/invoice-requests/${request?.id}/issue-manual`, {
        invoice_no: no.trim(),
        invoice_date: when,
        amount,
        more: more.map((row) => ({ invoice_no: row.no.trim(), amount: row.amount })),
      }),
    onSuccess: (inv) => {
      toast.push(more.length > 0 ? `已登錄 ${more.length + 1} 張發票` : `已登錄發票 ${inv.invoice_no}`, "success");
      onDone();
    },
    onError: (err) => toast.push(err instanceof Error ? err.message : "登錄失敗", "error"),
  });

  return (
    <Dialog
      open={request !== null}
      onClose={onClose}
      title="手動登錄發票"
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>取消</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!ready}
            onClick={() => save.mutate()}
          >
            確定登錄
          </Button>
        </div>
      }
    >
      <p className="m-0 text-sm text-[var(--text-2)]">
        在 Amego 後台或其他地方開好的發票,把號碼與日期填回來。
        <strong>這條路不會打電子發票 API</strong>,所以日期可以是過去的。
      </p>
      {request ? (
        <div className="m-0 rounded-[var(--radius)] bg-[var(--surface-2)] px-3 py-2 text-sm text-[var(--text)]">
          <div>{request.quote_no} {request.quote_title}</div>
          <div className="text-[var(--text-2)]">
            買受人:{request.buyer_name || "—"}・統一編號:{request.buyer_tax_id || "(個人,不打統編)"}
          </div>
        </div>
      ) : null}
      <Input label="發票日期" type="date" value={when} onChange={(e) => setWhen(e.target.value)} />
      <div className="flex items-end gap-2">
        <div className="flex-1"><Input label="發票號碼" value={no} onChange={(e) => setNo(e.target.value)} /></div>
        <div className="w-36"><MoneyField label="金額(含稅)" value={amount} onValueChange={setAmount} /></div>
        {/* 「+」:這張報價單財務開了不只一張發票,多登錄一張 */}
        <Button aria-label="多一張發票" title="多一張發票" icon={<Plus aria-hidden="true" size={16} />} onClick={addRow} />
      </div>
      {more.map((row, i) => (
        <div key={i} className="flex items-end gap-2">
          <div className="flex-1">
            <Input label={`第 ${i + 2} 張發票號碼`} value={row.no} onChange={(e) => setRow(i, { no: e.target.value })} />
          </div>
          <div className="w-36">
            <MoneyField label={`第 ${i + 2} 張金額`} value={row.amount} onValueChange={(next) => setRow(i, { amount: next })} />
          </div>
          <Button
            aria-label={`拿掉第 ${i + 2} 張`}
            icon={<X aria-hidden="true" size={16} />}
            onClick={() => setMore((rows) => rows.filter((_, j) => j !== i))}
          />
        </div>
      ))}
      {more.length > 0 && request ? (
        <p className={"m-0 text-sm " + (total.equals(toDecimal(request.amount).toDecimalPlaces(0)) ? "text-[var(--text-2)]" : "text-[var(--warn)]")}>
          {more.length + 1} 張合計 {money(total.toFixed(2))},申請金額 {money(request.amount)}
        </p>
      ) : null}
    </Dialog>
  );
}

/* ---------------------------------------------------------------- 已開立 / 逾期 */

function IssuedTab({
  list,
  overdue = false,
  onDone,
}: {
  list: InvoiceList | undefined;
  overdue?: boolean;
  onDone: () => void;
}) {
  const [voiding, setVoiding] = useState<Invoice | null>(null);
  const [paying, setPaying] = useState<Invoice | null>(null);
  const rows = list?.items ?? [];

  if (rows.length === 0) {
    return overdue ? (
      <EmptyState title="目前沒有逾期未收的發票" description="有請款日的發票過期又沒收到錢時會出現在這裡。" />
    ) : (
      <EmptyState title="還沒有開出去的發票" description="從「待開立」開立或登錄之後就會列在這裡。" />
    );
  }

  return (
    <>
      <p className="m-0 text-sm text-[var(--text-2)]">
        應收總額 <strong className="tabular-nums">{money(list?.total_outstanding ?? 0)}</strong>
        {/* 只加還欠錢的那幾張 —— 多收的不該把別張欠的抵掉,見後端 InvoiceListOut */}
      </p>
      <div className="w-full overflow-x-auto rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)]">
        <table
          aria-label={overdue ? "逾期未收清單" : "已開立清單"}
          className="w-full border-collapse text-left text-sm"
        >
          <thead>
            <tr className="whitespace-nowrap bg-[var(--surface-2)] text-xs font-medium text-[var(--text-2)]">
              <th scope="col" className="px-3 py-2">報價單</th>
              <th scope="col" className="px-3 py-2">發票號碼</th>
              <th scope="col" className="px-3 py-2">發票日</th>
              <th scope="col" className="px-3 py-2">買方</th>
              <th scope="col" className="px-3 py-2">金額</th>
              <th scope="col" className="px-3 py-2">已收</th>
              <th scope="col" className="px-3 py-2">未收</th>
              {overdue ? <th scope="col" className="px-3 py-2">逾期</th> : null}
              <th scope="col" className="px-3 py-2">狀態</th>
              <th scope="col" className="px-3 py-2">動作</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((inv) => (
              <tr key={inv.id} className="border-t border-[var(--border)]">
                {/* 這張發票是哪一張報價單的(業主 2026-10-16),點得進去 */}
                <td className="px-3 py-2">
                  {inv.quote_no ? <QuoteLink id={inv.quote_id} no={inv.quote_no} /> : "—"}
                </td>
                <td className="whitespace-nowrap px-3 py-2 font-medium text-[var(--text)]">{inv.invoice_no}</td>
                <td className="whitespace-nowrap px-3 py-2">{inv.invoice_date ?? "—"}</td>
                <td className="min-w-[10rem] px-3 py-2">{inv.buyer_name}</td>
                <td className="whitespace-nowrap px-3 py-2 tabular-nums">{money(inv.amount)}</td>
                <td className="whitespace-nowrap px-3 py-2 tabular-nums">{money(inv.paid_amount)}</td>
                <td className="whitespace-nowrap px-3 py-2 tabular-nums">{money(inv.outstanding)}</td>
                {overdue ? (
                  <td className="whitespace-nowrap px-3 py-2 tabular-nums">{inv.days_overdue} 天</td>
                ) : null}
                <td className="px-3 py-2">
                  {/* 狀態帶文字,不是只靠顏色。字樣由後端給(唯一的一份)。 */}
                  <Badge tone={inv.status === "VOID" ? "neutral" : "ok"}>{inv.status_label}</Badge>
                  {/* 測試發票要標得出來 —— 它有真的號碼、真的日期,不標的話
                      跟正式的分不出來。**不算營收**,但看得到。 */}
                  {inv.is_test ? (
                    <span className="ml-1 align-middle">
                      <Badge tone="warn">測試</Badge>
                    </span>
                  ) : null}
                  <div className="mt-0.5 text-xs text-[var(--text-3)]">{inv.source_label}</div>
                </td>
                <td className="whitespace-nowrap px-3 py-2">
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      icon={<HandCoins aria-hidden="true" size={14} />}
                      disabled={inv.status === "VOID"}
                      title={inv.status === "VOID" ? "已作廢的發票不該再收到錢" : undefined}
                      onClick={() => setPaying(inv)}
                    >
                      記一筆收款
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      icon={<Ban aria-hidden="true" size={14} />}
                      disabled={inv.status === "VOID"}
                      onClick={() => setVoiding(inv)}
                    >
                      作廢
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <VoidDialog key={`v${voiding?.id ?? 0}`} invoice={voiding}
                  onClose={() => setVoiding(null)}
                  onDone={() => { setVoiding(null); onDone(); }} />
      <PaymentDialog key={`p${paying?.id ?? 0}`} invoice={paying}
                     onClose={() => setPaying(null)}
                     onDone={() => { setPaying(null); onDone(); }} />
    </>
  );
}

/* ---------------------------------------------------------------- 作廢 */

function VoidDialog({
  invoice,
  onClose,
  onDone,
}: {
  invoice: Invoice | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [reason, setReason] = useState("");

  const run = useMutation({
    mutationFn: () => api.post<Invoice>(`/invoices/${invoice?.id}/void`, { reason: reason.trim() }),
    onSuccess: () => {
      toast.push("已作廢", "success");
      onDone();
    },
    onError: (err) => toast.push(err instanceof Error ? err.message : "作廢失敗", "error"),
  });

  return (
    <Dialog
      open={invoice !== null}
      onClose={onClose}
      title={`作廢發票 ${invoice?.invoice_no ?? ""}`}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>取消</Button>
          <Button
            variant="danger"
            loading={run.isPending}
            /* 理由是必填的 —— 作廢會送進國稅局,沒有理由日後查不出所以然。 */
            disabled={!reason.trim()}
            onClick={() => run.mutate()}
          >
            確定作廢
          </Button>
        </div>
      }
    >
      <p className="m-0 text-sm text-[var(--text-2)]">
        作廢會送進國稅局,而且沒有回頭路。理由請寫得日後看得懂。
      </p>
      <Input label="作廢理由" value={reason} onChange={(e) => setReason(e.target.value)} />
    </Dialog>
  );
}

/* ---------------------------------------------------------------- 收款 */

function PaymentDialog({
  invoice,
  onClose,
  onDone,
}: {
  invoice: Invoice | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [when, setWhen] = useState(today);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");

  const run = useMutation({
    mutationFn: () =>
      api.post<Invoice>(`/invoices/${invoice?.id}/payments`, {
        paid_on: when,
        amount,
        note,
      }),
    onSuccess: (inv) => {
      toast.push(
        inv.is_settled ? "已結清" : `已記一筆,未收 ${money(inv.outstanding)}`,
        "success",
      );
      onDone();
    },
    onError: (err) => toast.push(err instanceof Error ? err.message : "記錄失敗", "error"),
  });

  return (
    <Dialog
      open={invoice !== null}
      onClose={onClose}
      title={`記一筆收款 · ${invoice?.invoice_no ?? ""}`}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>取消</Button>
          <Button variant="primary" loading={run.isPending}
                  disabled={!amount.trim() || !when}
                  onClick={() => run.mutate()}>
            確定
          </Button>
        </div>
      }
    >
      <p className="m-0 text-sm text-[var(--text-2)]">
        未收 <strong className="tabular-nums">{money(invoice?.outstanding ?? 0)}</strong>。
        記錯了可以刪掉那一筆重記 —— <strong>不要記一筆負的</strong>。
      </p>
      <Input label="入帳日" type="date" value={when} onChange={(e) => setWhen(e.target.value)} />
      <MoneyField label="收款金額" value={amount} onValueChange={setAmount} />
      <Input label="備註" value={note} onChange={(e) => setNote(e.target.value)} />
    </Dialog>
  );
}
